<script lang="ts">
  import InputLegitimateInterest from '@components/fields/LegitimateInterest.svelte';
  import InputText from '@components/fields/Input.svelte';
  import InputRadio from '@components/fields/Radio.svelte';
  import Button from '@components/fields/Button.svelte';
  import InputSelect from '@components/fields/Select.svelte';
  import InputPhone from '@components/fields/Phone.svelte';
  import LoadingSpinner from '@components/global/LoadingSpinner.svelte';
  import { useEnquiryForm } from '../../feature/enquiry/hooks/useEnquiryForm';
  import { validationStore, errorStore } from '../../feature/enquiry/enquiry.store';
  import { generateEnquiryId } from '../../feature/enquiry/enquiry.util';
  import type { CountryCode, TelInputOptions } from '../../dev-helpers/phone/phone.schema';

  const {
    filteredLocations,
    selectedRegion,
    selectedLocation,
    selectedCountry,
    loading,
    sameCountryMessage,
    legitimateInterest,
    phoneValid,
    phoneError,
    REGIONS,
    handleRegionChange,
    handleLocationChange,
    handleSubmit
  } = useEnquiryForm();

  let enquiry_id = generateEnquiryId();
  let first_name = '';
  let last_name = '';
  let gender = '';
  let phone = '';
  let email = '';
  let goal = '';
  let marketing_consent: '1' = '1';
  let whatsapp_consent: '1' | '0' = '0';
  let sms_consent: '1' = '1';
  let privacy_opt: '1' = '1';
  let page_url = typeof window !== 'undefined' ? window.location.href : '';
  let isLoading = false;

  const handleKeydown = (field: string) => (e: Event) => {
    const target = e.target as HTMLInputElement;
    if (!target?.value) return;
    const value = target.value;

    switch (field) {
      case 'email':
        email = value;
        break;
      case 'first_name':
        first_name = value;
        break;
      case 'last_name':
        last_name = value;
        break;
      case 'phone':
        phone = value;
        break;
      case 'gender':
        gender = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
        break;
      case 'goal':
        goal = value;
        break;
    }
  };

  const handlePhoneChange = (country: CountryCode) => {
    selectedCountry.set(country);
  };

  const handleSameCountry = (country: CountryCode) => {
    sameCountryMessage.set(`You have already selected ${country}`);
    setTimeout(() => sameCountryMessage.set(null), 3000);
  };

  const onSubmit = async (e: Event) => {
    e.preventDefault();

    isLoading = true;

    const formData = {
      form_name: 'regional-enquiry-form',
      first_name,
      last_name,
      enquiry_id,
      email,
      marketing_consent,
      whatsapp_consent: ($legitimateInterest ? '1' : '0') as '1' | '0',
      sms_consent,
      legitimate_interest: ($legitimateInterest ? '1' : '0') as '1' | '0',
      privacy_opt,
      gender: gender.toLowerCase() as 'male' | 'female',
      phone: phone ?? '',
      goal,
      location_id: $selectedLocation?.key || '',
      reason: 'Personal Training',
      desired_city: '',
      desired_country: '',
      desired_region: $selectedRegion?.key || '',
      utm_id: '',
      utm_campaign: '',
      utm_content: '',
      utm_source: 'unbounce emebedded form',
      utm_medium: '',
      utm_term: '',
      page_url,
      location: $selectedLocation?.value || ''
    };

    try {
      await handleSubmit(formData);
    } finally {
      isLoading = false;
    }
  };

  $: if ($selectedLocation) {
    legitimateInterest.set(true);
    whatsapp_consent = '1';
  }

  const telInputOptions: TelInputOptions = {
    format: 'international'
  };
</script>

<section class="form-wrapper up-embed-form-wrapper">
  <div class="up-embed-enquiry-container">
    <div class="up-embed-enquiry">
      <div>
        <h2 class="up-embed-enquiry__header">Enquire Now</h2>
        <p class="up-embed-enquiry__subtitle">For more details and pricing information</p>
      </div>

      {#if $errorStore}
        <p class="up-embed-enquiry__error">{$errorStore}</p>
      {/if}

      <div class="up-embed-form-row">
        <InputText
          label="First Name"
          id="first_name"
          type="text"
          required={true}
          error={$validationStore.first_name}
          updated={handleKeydown('first_name')}
          changed={() => {}}
        />
        <InputText
          label="Last Name"
          id="last_name"
          type="text"
          required={true}
          error={$validationStore.last_name}
          updated={handleKeydown('last_name')}
          changed={() => {}}
        />
      </div>

      <InputText
        label="Email Address"
        id="email"
        type="email"
        required={true}
        placeholder="Email Address"
        error={$validationStore.email}
        updated={handleKeydown('email')}
        changed={() => {}}
      />

      <InputRadio
        label="Gender"
        id="gender"
        className="up-embed-inline-radio"
        required={true}
        error={$validationStore.gender}
        options={[
          { label: 'Male', value: 'Male' },
          { label: 'Female', value: 'Female' }
        ]}
        changed={handleKeydown('gender')}
      />

      <div class="up-embed-form-row">
        <div class="up-embed-select-wrapper">
          <InputSelect
            label="Your Region"
            id="region"
            required={true}
            options={REGIONS}
            error={$validationStore.region}
            changed={handleRegionChange}
            placeholder="Select your region"
            className="up-embed-region-select"
            selectedOption={$selectedRegion}
          />
        </div>

        <div class="up-embed-select-wrapper">
          <InputSelect
            label="Location"
            id="location"
            required={true}
            options={$filteredLocations.map((location) => ({
              key: location.location_id,
              value: location.location_name
            }))}
            changed={handleLocationChange}
            placeholder="Select your location"
            className="up-embed-location-select"
            selectedOption={$selectedLocation}
            disabled={!$selectedRegion}
          />
        </div>
      </div>

      <InputRadio
        label="Tell us about your goal?"
        id="goal"
        required={true}
        error={$validationStore.goal}
        options={[
          { label: 'Lose weight', value: 'lose_weight' },
          { label: 'Improve my health', value: 'improve_my_health' },
          { label: 'Build muscle', value: 'build_muscle' },
          { label: "I'm not sure", value: 'not_sure' }
        ]}
        changed={handleKeydown('goal')}
      />

      <InputPhone
        label="Phone Number"
        id="phone-input"
        required={false}
        bind:value={phone}
        selectedCountry={$selectedCountry}
        options={telInputOptions}
        error={$phoneError}
        valid={$phoneValid}
        onChange={handlePhoneChange}
        onSame={handleSameCountry}
      />

      {#if $selectedLocation}
        <div class="up-embed-legitimate-interest">
          <InputLegitimateInterest
            id="legitimate_interest"
            options={[
              { label: 'Get WhatsApp reminders', value: '1', checked: $legitimateInterest }
            ]}
            changed={(newValue) => {
              legitimateInterest.set(newValue);
              whatsapp_consent = newValue ? '1' : '0';
            }}
            description="Please untick the box to opt-out if you do not wish to receive WhatsApp updates."
            secondaryDescription="By registering your interest you agree that Ultimate Performance may contact you by SMS or email channels. To opt out please tick."
          />
        </div>
      {/if}

      <span class="up-embed-enquiry__disclaimer">
        By clicking 'Submit' I have read and understood the
        <a href="https://ultimateperformance.com/privacy-policy/" target="_blank">privacy policy</a>
        and
        <a
          href="https://ultimateperformance.com/personal-training-terms-conditions/"
          target="_blank">terms and conditions.</a
        >
      </span>

      <div class="up-embed-submit-wrapper">
        {#if isLoading}
          <div class="up-embed-loading">
            <LoadingSpinner size="24px" />
            <span class="ml-2">Submitting...</span>
          </div>
        {:else}
          <Button
            id="submit"
            type="submit"
            className="w-60 mx-auto up-embed-btn-primary bg-[#e40032] px-6 py-4 rounded-sm text-md font-[900]"
            label="Submit"
            style="primary"
            buttonclick={onSubmit}
          />
        {/if}
      </div>
    </div>
  </div>
</section>

<style lang="scss">
  @use '../../../styles/enquiry';

  .up-embed-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    color: #e40032;
  }

  .ml-2 {
    margin-left: 0.5rem;
  }
</style>
